import { OnInit, OnDestroy } from '@angular/core'
import { Observable, Subscription, combineLatest, from, of } from 'rxjs'
import { mergeMap, take, map, toArray } from 'rxjs/operators'
import { AuthService } from '@shared/services/auth.service'
import { EventService } from '@shared/services/event.service'
import { AttendeesService } from '@shared/services/attendees.service'
import { AppUser } from '@shared/models/app-user.model'
import { EventModel } from '@shared/models/event.model'
import { PublicPageTitleService } from '@public/app/services/public-page-title.service'
import { LoadingIndicatorService } from '@public/app/services/loading-indicator.service'
import { ToastService } from '@public/app/services/toast.service'

export abstract class BaseEventListComponent implements OnInit, OnDestroy {
  eventList: EventModel[] = []
  subscriptions: Subscription[] = []
  pageTitle = ''

  protected constructor(
    protected authService: AuthService,
    protected eventService: EventService,
    protected attendeesService: AttendeesService,
    protected publicPageTitleService: PublicPageTitleService,
    protected loadingIndicatorService: LoadingIndicatorService,
    protected toastService: ToastService
  ) {}

  protected setEventRegistrationStatus(event: EventModel, user: AppUser | null): Observable<EventModel> {
    if (!user) {
      event.isRegistered = false
      return of(event)
    }

    return this.attendeesService.isAlreadyAppliedForEvent(user.id, event.id).pipe(
      take(1),
      map(isApplied => {
        event.isRegistered = isApplied
        return event
      })
    )
  }

  ngOnInit(): void {
    const sub = combineLatest([this.eventService.getFutureApprovedEvents()])
      .pipe(
        mergeMap(([events]) => {
          return from(events).pipe(
            mergeMap(event => this.setEventRegistrationStatus(event, this.authService.getCurrentUser())),
            toArray(),
            map(eventsWithRegistration => this.transformEvents(eventsWithRegistration))
          )
        })
      )
      .subscribe(events => {
        this.eventList = events
      })

    this.subscriptions.push(sub)
    this.publicPageTitleService.setPageTitle(this.pageTitle)
  }

  onUnregister(event: EventModel): void {
    this.loadingIndicatorService.showLoading()
    if (!this.authService.getCurrentUser()) {
      // console.log('Missing feature: handle no user')
      this.loadingIndicatorService.hideLoading()
      return
    }
    this.attendeesService
      .cancelApplicationToEvent(this.authService.getCurrentUser().id, event.id, event.organiserId)
      .subscribe({
        next: resp => {
          this.setEventRegistrationStatus(event, this.authService.getCurrentUser()).subscribe(updatedEvent => {
            this.updateEventListAfterUnregister(updatedEvent)
            this.toastService.showSuccess('Eseményről való lejelentkezés sikeres')
          })
        },
        error: err => {
          const errorMsg = this.translateErrorMessage(err?.message)
          this.loadingIndicatorService.hideLoading()
          this.toastService.showError(errorMsg)
        },
        complete: () => {
          this.loadingIndicatorService.hideLoading()
        },
      })
  }

  protected transformEvents(events: EventModel[]): EventModel[] {
    return events
  }

  protected updateEventListAfterUnregister(event: EventModel): void {
    // console.log('Missing feature: handle update of event list after unregister in child class if needed')
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe())
  }

  protected translateErrorMessage(message: string): string {
    if (message === 'No user name was provided.') {
      return 'Nincs felhasználónév megadva.'
    } else if (message === 'Already applied for this event') {
      return 'Már regisztráltál erre az eseményre.'
    } else if (message === 'Event is not exists') {
      return 'Ez az esemény nem létezik.'
    } else if (message === 'No more users can apply for this event. It has reached the max.') {
      return 'Az esemény elérte a maximális résztvevők számát.'
    } else if (message === 'No such an organization') {
      return 'Nincs ilyen szervezet.'
    } else if (
      message === 'only authenticated users can go to an Event ' ||
      message === 'only authenticated users can remove application of an Event'
    ) {
      return 'Csak bejelentkezett felhasználók használhatják ezt a funkciót.'
    } else {
      console.log('Missing feature: more error translation')
      return 'Ismeretlen hiba történt.'
    }
  }
}
